import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary — catches errors in any child component tree,
 * logs them, and displays a fallback UI instead of a blank screen.
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console in dev; in production, send to Sentry/analytics
    console.error('[ErrorBoundary] Uncaught error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #0a0814 0%, #1a1040 50%, #0d1930 100%)',
          padding: '20px',
        }}>
          <div style={{
            background: 'rgba(13, 25, 48, 0.8)',
            backdropFilter: 'blur(20px)',
            borderRadius: '20px',
            padding: '40px',
            maxWidth: '500px',
            width: '100%',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <h2 style={{
              fontSize: '22px',
              fontWeight: 'bold',
              color: '#fff',
              marginBottom: '12px',
            }}>
              Что-то пошло не так
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#aaa',
              lineHeight: 1.6,
              marginBottom: '24px',
            }}>
              Произошла непредвиденная ошибка. Мы уже работаем над её исправлением.
            </p>
            {this.state.error && (
              <details style={{
                textAlign: 'left',
                marginBottom: '20px',
                padding: '12px',
                background: 'rgba(0, 0, 0, 0.3)',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#888',
                fontFamily: 'monospace',
                maxHeight: '150px',
                overflow: 'auto',
              }}>
                <summary style={{ cursor: 'pointer', color: '#666' }}>Технические детали</summary>
                <pre style={{ margin: '8px 0 0', whiteSpace: 'pre-wrap' }}>
                  {this.state.error.message}
                </pre>
              </details>
            )}
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'center',
            }}>
              <button
                onClick={this.handleGoHome}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  background: 'transparent',
                  color: '#d4af37',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                На главную
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '12px 24px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#7b68ee',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                Перезагрузить
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
